import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { fetchComision } from '../lib/comision';
import { PAGES } from '../seo';

// Tarjeta de la mesa directiva (organigrama)
const CargoCard = ({ p, destacado, className = '' }) => {
  if (!p) return null;
  return (
    <div
      className={`relative z-10 w-full rounded-2xl px-5 py-5 flex flex-col items-center text-center transition-shadow ${
        destacado
          ? 'bg-gradient-to-br from-blue-600 to-blue-800 text-white shadow-xl shadow-blue-600/25'
          : 'bg-white border-2 border-blue-100 shadow-sm hover:shadow-md'
      } ${className}`}
    >
      <div className="min-w-0">
        <p className={`text-xs font-semibold uppercase tracking-wider ${destacado ? 'text-blue-100' : 'text-blue-600'}`}>{p.cargo}</p>
        <p className={`font-bold leading-snug ${destacado ? 'text-xl sm:text-2xl' : 'text-lg text-gray-900'}`}>{p.nombre}</p>
        {p.empresa && <p className={`text-sm ${destacado ? 'text-blue-100' : 'text-gray-600'}`}>{p.empresa}</p>}
      </div>
    </div>
  );
};

const LineaV = ({ h = 'h-8' }) => <div className={`w-0.5 ${h} bg-blue-200 mx-auto`} aria-hidden="true" />;

// Listado numerado (vocales y revisores)
const Lista = ({ titulo, personas }) => {
  if (!personas.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <h4 className="px-5 py-3 bg-gray-50 border-b border-gray-200 text-sm font-bold uppercase tracking-wider text-gray-700">{titulo}</h4>
      <ul className="divide-y divide-gray-100">
        {personas.map((p) => (
          <li key={p.cargo + p.nombre} className="px-5 py-3.5 flex items-center gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
              {p.orden || '•'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900 leading-snug">{p.nombre}</p>
              {p.empresa && <p className="text-sm text-gray-600">{p.empresa}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ComisionDirectiva = ({ initialData = null }) => {
  const [cd, setCd] = useState(initialData);
  const [error, setError] = useState(false);

  const cargar = () => {
    setError(false);
    setCd(null);
    fetchComision().then(setCd).catch(() => setError(true));
  };

  useEffect(() => {
    document.title = PAGES.comision.title;
    window.scrollTo(0, 0);
    cargar();
    return () => { document.title = PAGES.home.title; };
  }, []);

  const m = cd ? cd.mesa : {};

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main id="contenido-principal" tabIndex={-1} className="pt-20 lg:pt-24 focus:outline-none">
        <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Volver al inicio
            </Link>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">Comisión Directiva</h1>
            <p className="mt-2 text-base sm:text-lg text-gray-600">Autoridades de la Unión Industrial Quilmes</p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 pb-20">
          {error && (
            <div className="text-center bg-white border border-gray-200 rounded-2xl p-10">
              <p className="text-gray-700">No pudimos cargar la Comisión Directiva en este momento.</p>
              <button onClick={cargar} className="mt-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">
                <RefreshCw className="w-4 h-4" /> Reintentar
              </button>
            </div>
          )}

          {!error && !cd && (
            <div className="max-w-md mx-auto space-y-6 animate-pulse" aria-busy="true">
              <div className="h-24 bg-blue-100 rounded-2xl" />
              <div className="h-20 bg-white border border-gray-200 rounded-2xl" />
              <div className="grid grid-cols-2 gap-4"><div className="h-20 bg-white rounded-2xl" /><div className="h-20 bg-white rounded-2xl" /></div>
            </div>
          )}

          {cd && (
            <>
              {/* Organigrama: mesa directiva */}
              <section aria-label="Mesa directiva" data-testid="organigrama">
                <div className="max-w-md mx-auto">
                  <CargoCard p={m.presidente} destacado />
                  {m.vicepresidente && (
                    <>
                      <LineaV />
                      <CargoCard p={m.vicepresidente} />
                    </>
                  )}
                </div>

                {(m.secretario || m.tesorero) && (
                  <>
                    <LineaV />
                    {/* Línea horizontal que abre las dos áreas (solo en pantallas anchas) */}
                    <div className="hidden md:block relative max-w-4xl mx-auto h-8" aria-hidden="true">
                      <div className="absolute top-0 left-1/4 right-1/4 h-0.5 bg-blue-200" />
                      <div className="absolute top-0 left-1/4 w-0.5 h-8 bg-blue-200" />
                      <div className="absolute top-0 right-1/4 w-0.5 h-8 bg-blue-200" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 md:gap-10 max-w-4xl mx-auto">
                      {[
                        { area: 'Secretaría', titular: m.secretario, adjunto: m.prosecretario },
                        { area: 'Tesorería', titular: m.tesorero, adjunto: m.protesorero },
                      ].map(({ area, titular, adjunto }) =>
                        titular || adjunto ? (
                          <div key={area} className="bg-blue-50/60 border border-blue-100 rounded-3xl p-4 sm:p-5">
                            <CargoCard p={titular} />
                            {adjunto && (
                              <>
                                <LineaV h="h-5" />
                                <CargoCard p={adjunto} />
                              </>
                            )}
                          </div>
                        ) : null
                      )}
                    </div>
                  </>
                )}
              </section>

              {/* Vocales */}
              {(cd.vocalesTitulares.length > 0 || cd.vocalesSuplentes.length > 0) && (
                <section className="mt-8 sm:mt-10" aria-label="Vocales">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Lista titulo="Vocales titulares" personas={cd.vocalesTitulares} />
                    <Lista titulo="Vocales suplentes" personas={cd.vocalesSuplentes} />
                  </div>
                </section>
              )}

              {/* Revisores de cuentas */}
              {(cd.revisoresTitulares.length > 0 || cd.revisoresSuplentes.length > 0) && (
                <section className="mt-8 sm:mt-10" aria-label="Comisión Revisora de Cuentas">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Lista titulo="Revisores de cuentas titulares" personas={cd.revisoresTitulares} />
                    <Lista titulo="Revisores de cuentas suplentes" personas={cd.revisoresSuplentes} />
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
